import { useState, useRef, useEffect, useId, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * An autocomplete option. A bare string is the value the parent approves
 * (e.g. `@school.org`). The object form adds an optional human `label`
 * (e.g. a platform-relay school's display name) shown above the value in
 * the dropdown; selecting it still fills the `value`, never the label.
 */
export type SenderComboboxOption = string | { value: string; label?: string };

interface NormalizedOption {
  value: string;
  label?: string;
}

export interface SenderComboboxProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void;
  options: SenderComboboxOption[];
  placeholder?: string;
  ariaLabel?: string;
  label?: string;
  error?: string;
}

const MAX_OPTIONS = 8;
const DROPDOWN_GAP = 4; // px between input and dropdown
const DROPDOWN_MAX_HEIGHT = 240; // px — must match `max-h-60` Tailwind class

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  placement: 'below' | 'above';
}

function normalizeOption(opt: SenderComboboxOption): NormalizedOption {
  return typeof opt === 'string' ? { value: opt } : opt;
}

// Rank 0 = the query is a prefix of the value (ignoring a leading `@`) or of
// the label; rank 1 = a mid-string match. Lower sorts first. A stable sort
// then preserves the caller's original ordering within a rank.
function matchRank(opt: NormalizedOption, q: string): number {
  const v = opt.value.toLowerCase();
  const core = v.startsWith('@') ? v.slice(1) : v;
  const l = opt.label?.toLowerCase();
  if (v.startsWith(q) || core.startsWith(q) || (l && l.startsWith(q))) return 0;
  return 1;
}

function optionMatches(opt: NormalizedOption, q: string): boolean {
  return (
    opt.value.toLowerCase().includes(q) ||
    (opt.label?.toLowerCase().includes(q) ?? false)
  );
}

export function SenderCombobox({
  value,
  onChange,
  onSubmit,
  options,
  placeholder,
  ariaLabel,
  label,
  error,
}: SenderComboboxProps) {
  const listId = useId();
  const inputId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  /**
   * Gates the late re-open below. A ref, not state: it must not itself
   * trigger a render, and the effect that reads it already re-runs on the
   * `shouldOpen` transition we care about.
   */
  const focusedRef = useRef(false);

  const query = value.toLowerCase();
  const filtered: NormalizedOption[] = options
    .map(normalizeOption)
    .filter((opt) => optionMatches(opt, query))
    // Prefix matches first; ties keep the caller's order (stable sort).
    .sort((a, b) => matchRank(a, query) - matchRank(b, query))
    .slice(0, MAX_OPTIONS);

  const shouldOpen = value.length > 0 && filtered.length > 0;

  // Compute dropdown placement from the input's viewport rect. Uses fixed
  // positioning + portal to escape the AppShell scroll container and the
  // sticky onboarding footer (`z-[100]`). Flips above the input when there
  // isn't enough space below.
  const updatePosition = () => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const placement: 'below' | 'above' =
      spaceBelow < DROPDOWN_MAX_HEIGHT + DROPDOWN_GAP && spaceAbove > spaceBelow
        ? 'above'
        : 'below';
    setPosition({
      top:
        placement === 'below'
          ? rect.bottom + DROPDOWN_GAP
          : rect.top - DROPDOWN_GAP,
      left: rect.left,
      width: rect.width,
      placement,
    });
  };

  // Recompute placement on open and whenever the viewport / scroll moves.
  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const handler = () => updatePosition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    return () => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    };
  }, [open]);

  useEffect(() => {
    if (!shouldOpen) {
      setOpen(false);
      setHighlightedIndex(null);
      return;
    }
    // Re-open once suggestions actually exist. `open` used to be set only
    // from the input's own handlers, and both read `filtered` from the
    // render they were created in — so when `options` arrived AFTER a
    // keystroke, the dropdown stayed shut with no path back open. That is
    // real in Settings, where the option list is the platform catalogue
    // minus the household's already-approved senders and so waits on a
    // fetch: type before it resolves and autocomplete never appears again
    // until you edit the field a second time.
    if (focusedRef.current) setOpen(true);
  }, [shouldOpen]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleMouseDown(e: MouseEvent) {
      const target = e.target as Node;
      const insideContainer = containerRef.current?.contains(target) ?? false;
      // The listbox is portal-rendered into document.body, so it sits
      // outside containerRef. Treat clicks inside the listbox as in-bounds
      // so option selection isn't swallowed by the close-on-outside path.
      const insideListbox = listboxRef.current?.contains(target) ?? false;
      if (!insideContainer && !insideListbox) {
        setOpen(false);
        setHighlightedIndex(null);
      }
    }
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open]);

  function handleSelect(opt: NormalizedOption) {
    onChange(opt.value);
    setOpen(false);
    setHighlightedIndex(null);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setOpen(false);
      setHighlightedIndex(null);
      return;
    }

    if (!open || filtered.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault();
        onSubmit();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev === null || prev >= filtered.length - 1 ? 0 : prev + 1,
      );
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev === null || prev <= 0 ? filtered.length - 1 : prev - 1,
      );
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (
        highlightedIndex !== null &&
        filtered[highlightedIndex] !== undefined
      ) {
        handleSelect(filtered[highlightedIndex]);
      } else {
        onSubmit();
      }
      return;
    }
  }

  const activedescendant =
    highlightedIndex !== null ? `${listId}-${highlightedIndex}` : undefined;

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-1.5 w-full"
    >
      {label && (
        <label
          htmlFor={inputId}
          className="text-sm font-medium text-foreground leading-5 flex items-center gap-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          aria-activedescendant={activedescendant}
          aria-label={!label ? ariaLabel : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : undefined}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (e.target.value.length > 0 && filtered.length > 0) {
              setOpen(true);
            } else {
              setOpen(false);
            }
            setHighlightedIndex(null);
          }}
          onFocus={() => {
            focusedRef.current = true;
            if (shouldOpen) setOpen(true);
          }}
          onBlur={() => {
            // Only stops the late re-open above; closing on the way out is
            // still the click-outside listener's job, so selecting an option
            // (mousedown, which precedes blur) is unaffected.
            focusedRef.current = false;
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={[
            'w-full rounded-sm border bg-white text-foreground text-sm leading-5',
            'px-2.5 py-2',
            'placeholder:text-text-placeholder',
            'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
            'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
            'transition-colors duration-150',
            error ? 'border-border-error' : 'border-border',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      </div>
      {open &&
        filtered.length > 0 &&
        position &&
        typeof document !== 'undefined' &&
        createPortal(
          <ul
            ref={listboxRef}
            id={listId}
            role="listbox"
            style={{
              position: 'fixed',
              top:
                position.placement === 'below'
                  ? position.top
                  : Math.max(
                      DROPDOWN_GAP,
                      position.top -
                        Math.min(DROPDOWN_MAX_HEIGHT, filtered.length * 36 + 4),
                    ),
              left: position.left,
              width: position.width,
              maxHeight: DROPDOWN_MAX_HEIGHT,
            }}
            className="z-[200] overflow-y-auto rounded-sm border border-border bg-card shadow-md"
          >
            {filtered.map((opt, i) => (
              <li
                key={opt.value}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === highlightedIndex}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(opt);
                }}
                onMouseEnter={() => setHighlightedIndex(i)}
                className={[
                  'px-3 py-2 text-sm cursor-pointer text-foreground',
                  i === highlightedIndex ? 'bg-muted' : 'hover:bg-muted/50',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {opt.label ? (
                  <span className="flex flex-col">
                    <span className="font-medium leading-5 truncate">
                      {opt.label}
                    </span>
                    <span className="text-xs text-muted-foreground leading-4 truncate">
                      {opt.value}
                    </span>
                  </span>
                ) : (
                  opt.value
                )}
              </li>
            ))}
          </ul>,
          document.body,
        )}
      {error && (
        <p
          id={`${inputId}-error`}
          className="text-sm text-danger leading-5"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
}
